"use client";

import { useEffect, useState } from "react";
import {
  useHydrationSafeFeatureFlagState,
  type HydrationSafeFeatureFlagState,
} from "@/hooks/useHydrationSafeFeatureFlagEnabled";
import { useOrg } from "@/hooks/useOrg";
import { isScriptsApiV2 } from "@/utils/org/scriptsApiVersion";

export const SCRIPT_VERSIONING_FEATURE_FLAG = "ui-enable-script-versioning";

function useOrgFeaturesReady(): { ready: boolean; isV2: boolean } {
  const { org, orgFeaturesLoaded } = useOrg();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Real OrgContext sets this false until GetOrgFeatures finishes. Test mocks
  // that omit the field still count as loaded so they keep historical behavior.
  const featuresReady = orgFeaturesLoaded ?? true;
  const ready = mounted && Boolean(org?.id) && featuresReady;
  return { ready, isV2: ready && isScriptsApiV2(org?.features) };
}

/**
 * Mount-gated so versioning nav/routes stay off on SSR and first paint,
 * then appear after mount when `organization.features.scriptsApiVersion` is v2
 * (avoids React #418). `getDefaultOrg()` (no id) is not treated as v1 for redirects.
 * Cached org id is not enough — wait until `GetOrgFeatures()` has finished so a
 * stale localStorage org cannot bounce a v2 deep link to the legacy editor.
 */
export function useScriptVersioningEnabled(): boolean {
  return useScriptVersioningFlagState().enabled;
}

/**
 * Same as {@link useScriptVersioningEnabled}, plus `ready` so callers can
 * defer versioning-gated network (e.g. DE panel) until org features have resolved.
 */
export function useScriptVersioningFlagState(): HydrationSafeFeatureFlagState {
  const { ready, isV2 } = useOrgFeaturesReady();
  return { ready, enabled: isV2 };
}

/**
 * PostHog `ui-enable-script-versioning` **and** the org is not yet on scripts API v2.
 * Hidden on SSR/first paint until both PostHog and org features are ready.
 */
export function useScriptVersioningUpgradeBannerEnabled(): boolean {
  const posthog = useHydrationSafeFeatureFlagState(SCRIPT_VERSIONING_FEATURE_FLAG);
  const { ready: orgReady, isV2 } = useOrgFeaturesReady();
  return posthog.ready && orgReady && posthog.enabled && !isV2;
}
